/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { resolve } from 'rsvp';
import { task } from 'ember-concurrency';
import { MetricTypes, Entities } from 'cio/utils/metrics/common';

import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import MetricsService from 'cio/services/metrics';
import RouterService from '@ember/routing/router-service';
import SegmentModel from 'cio/models/segment';
import TagModel from 'cio/models/tag';

interface SegmentHeaderArgs {
  hasImportsInProgress: boolean;
  isImportingCSV: boolean;
  isLoadingPeopleCount: boolean;
  peopleCount: number;
  segment: SegmentModel;
  segmentMembershipChangeExportRange: any;
  didUpdateSegment: () => unknown;
  reloadSegmentAndPoll: () => unknown;
}

export default class SegmentHeader extends Component<SegmentHeaderArgs> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service('metrics') metricsService!: MetricsService;
  @service router!: RouterService;
  @service segmentActions!: any;

  get peopleCount() {
    return this.args.peopleCount ?? 0;
  }

  setAttrAndSaveSegmentTask = task({ drop: true }, async (attr: keyof SegmentModel, value: string) => {
    let { segment } = this.args;
    segment.set(attr, value);

    // If the segment is new, we don't want to save it because conditions may
    // not be set and that will throw an error.
    if (segment.get('isNew')) {
      return resolve();
    }

    return await segment.save();
  });

  toggleArchiveTask = task({ drop: true }, async () => {
    let { segment } = this.args;
    let action = segment.archived ? 'unarchive' : 'archive';
    await this.segmentActions[action]([segment.id]);
    this.metricsService.clearMetrics(MetricTypes.SegmentMembershipMetric, Entities.SEGMENT, segment.id);
    await this.args.reloadSegmentAndPoll();
  });

  clearSegmentTask = task({ drop: true }, async () => {
    try {
      await this.args.segment.clear();
      await this.args.didUpdateSegment();
      this.alerts.success('segment.cleared', 'Segment cleared!');
    } catch (errors) {
      this.apiErrors.report(errors);
    }
  });

  deleteSegmentTask = task({ drop: true }, async () => {
    try {
      await this.args.segment.destroyRecord();
      this.router.transitionTo('segments');
    } catch (errors) {
      this.args.segment.rollbackAttributes();
      this.apiErrors.report(errors);
    }
  });

  @action
  onEditStart(type: string) {
    this.analytics.track('segment', 'edit_started', {
      model: this.args.segment,
      type,
    });
  }

  @action
  addTag(tag: TagModel) {
    let { segment } = this.args;

    if (segment.get('isNew')) {
      return segment.set('tags', [...(segment.tags.content as TagModel[]), tag]);
    }

    return segment.addTags([tag]);
  }

  @action
  removeTag(tag: TagModel) {
    let { segment } = this.args;

    if (segment.get('isNew')) {
      return segment.set(
        'tags',
        (segment.tags.content as TagModel[]).filter((t) => t !== tag),
      );
    }

    return segment.removeTag(tag);
  }
}
